import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FileSpreadsheet, FileText, Info, Loader2, ExternalLink, Users, TrendingUp } from 'lucide-react';
import { DepartmentTrend } from './DepartmentTrend';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Money } from '../ui/Money';

const API = process.env.REACT_APP_BACKEND_URL;
const SOURCE_NOTE = {
  live: 'live from current staff salaries',
  payroll: 'from the saved monthly payroll',
  salary_snapshot: "from the month-end salary snapshot (each person's saved salary)",
  estimate: 'estimated per person from promotion history or the nearest saved payroll (no staff-level payroll saved for this month)',
};
const ChangeCell = ({ v, base }) => {
  const pct = base ? Math.round((v / base) * 1000) / 10 : null;
  return (
    <span className={`whitespace-nowrap ${v > 0 ? 'text-red-600 font-semibold' : v < 0 ? 'text-green-600 font-semibold' : 'text-gray-400'}`}>
      {v > 0 ? '+' : v < 0 ? '−' : ''}<Money value={Math.abs(v)} />
      {v !== 0 && pct != null && <span className="ml-1 text-[11px] font-medium" data-testid="change-pct">({v > 0 ? '+' : '−'}{Math.abs(pct)}%)</span>}
    </span>
  );
};

export const DepartmentStaffDialog = ({ department, month, year, onClose }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState('');
  const [tab, setTab] = useState('staff');
  const [onlyChanged, setOnlyChanged] = useState(false);
  const params = { department, month, year };
  useEffect(() => {
    if (!department) return;
    setD(null);
    setTab('staff');
    axios.get(`${API}/api/payroll/comparison/department`, { params }).then((r) => setD(r.data)).catch(() => setD({ rows: [], error: true }));
  }, [department, month, year]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async (format) => {
    setBusy(format);
    try {
      const r = await axios.get(`${API}/api/payroll/comparison/department`, { params: { ...params, format }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      Object.assign(document.createElement('a'), { href: url, download: `payroll_${department}_${month}_${year}.${format}` }).click();
      URL.revokeObjectURL(url);
    } finally { setBusy(''); }
  };
  const cy = d?.current_year; const py = d?.previous_year; const mn = d?.month_name?.slice(0, 3);
  const notes = d && !d.error ? [[cy, d.current_source, d.saved_dept_total?.current], [py, d.previous_source, d.saved_dept_total?.previous]] : [];

  return (
    <Dialog open={!!department} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="dept-staff-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2 pr-6">
            <span data-testid="dept-staff-title">{department}</span>
            {d && !d.error && <span className="text-sm font-normal text-gray-500">{d.totals.staff} staff · {d.month_name} {cy} vs {py}</span>}
            <span className="ml-auto flex gap-2">
              <Button size="sm" variant="outline" disabled={!d || !!busy} onClick={() => download('xlsx')} data-testid="dept-staff-xlsx">{busy === 'xlsx' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1" />}Excel</Button>
              <Button size="sm" variant="outline" disabled={!d || !!busy} onClick={() => download('pdf')} data-testid="dept-staff-pdf">{busy === 'pdf' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileText className="h-4 w-4 mr-1" />}PDF</Button>
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit" data-testid="dept-dialog-tabs">
          {[['staff', 'Staff', Users], ['trend', '12-month trend', TrendingUp]].map(([k, l, Icon]) => (
            <button key={k} type="button" onClick={() => setTab(k)} data-testid={`dept-tab-${k}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${tab === k ? 'bg-white shadow-sm text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-800'}`}><Icon className="h-4 w-4" />{l}</button>
          ))}
        </div>
        {tab === 'trend' && <DepartmentTrend department={department} month={month} year={year} />}
        {tab === 'staff' && !d && <p className="text-sm text-gray-400 py-8 text-center">Loading staff…</p>}
        {tab === 'staff' && d?.error && <p className="text-sm text-red-600 py-8 text-center">Could not load this department.</p>}
        {tab === 'staff' && d && !d.error && (<>
          <div className="space-y-1">
            {notes.map(([y, src, saved]) => (
              <p key={y} className="text-[11px] text-gray-500 flex items-start gap-1" data-testid={`dept-staff-source-${y}`}>
                <Info className="h-3 w-3 mt-0.5 shrink-0" />{mn} {y}: {SOURCE_NOTE[src]}
                {src === 'estimate' && saved != null && <> — saved department total was <b className="mx-1"><Money value={saved} /></b></>}
              </p>
            ))}
            {(() => { const n = d.rows.filter(r => r.change !== 0 && r.current != null && r.previous != null).length; return (
              <label className="flex items-center gap-2 text-xs text-gray-600 pt-1 cursor-pointer w-fit" data-testid="dept-only-changed-label">
                <input type="checkbox" checked={onlyChanged} onChange={e => setOnlyChanged(e.target.checked)} data-testid="dept-only-changed" />
                Only show salary changes <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 font-semibold" data-testid="dept-changed-count">{n} changed</span>
              </label>); })()}
          </div>
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm" data-testid="dept-staff-table">
              <thead className="bg-slate-50 text-[11px] uppercase text-gray-500">
                <tr><th className="text-left px-3 py-2">Code</th><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Position</th><th className="text-left px-3 py-2">DOJ</th>
                  <th className="text-right px-3 py-2">{mn} {cy}</th><th className="text-right px-3 py-2">{mn} {py}</th><th className="text-right px-3 py-2">Change</th></tr>
              </thead>
              <tbody className="divide-y">
                {d.rows.filter(r => !onlyChanged || (r.change !== 0 && r.current != null && r.previous != null)).map((r) => {
                  const changed = r.change !== 0 && r.current != null && r.previous != null;
                  return (
                  <tr key={r.employee_id} className={changed ? (r.change > 0 ? 'bg-amber-50 hover:bg-amber-100/70' : 'bg-emerald-50 hover:bg-emerald-100/70') : 'hover:bg-violet-50/40'}
                    data-testid={`dept-staff-row-${r.employee_code}`} data-changed={changed ? 'yes' : 'no'}>
                    <td className="px-3 py-2 text-gray-500">{r.employee_code}</td>
                    <td className="px-3 py-2 font-medium">
                      {r.name === '(removed)' ? r.name : (
                        <a href={`/employees?focus=${encodeURIComponent(r.employee_id)}`} target="_blank" rel="noopener noreferrer"
                          className="text-violet-700 hover:text-violet-900 hover:underline underline-offset-2 inline-flex items-center gap-1" title="Open profile in a new tab" data-testid={`dept-staff-profile-${r.employee_code}`}>
                          {r.name}<ExternalLink className="h-3 w-3 opacity-60" />
                        </a>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-600">{r.position}</td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{r.join_date || '—'}</td>
                    <td className="px-3 py-2 text-right text-blue-700 font-medium">{r.current == null ? '—' : <Money value={r.current} />}</td>
                    <td className="px-3 py-2 text-right text-gray-500">{r.previous == null ? '—' : <Money value={r.previous} />}</td>
                    <td className="px-3 py-2 text-right"><ChangeCell v={r.change} base={r.current != null ? r.previous : null} /></td>
                  </tr>
                  );
                })}
                {!d.rows.length && <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-500">No staff found for this department in either period.</td></tr>}
              </tbody>
              <tfoot><tr className="bg-amber-50 font-semibold" data-testid="dept-staff-totals">
                <td className="px-3 py-2" colSpan={4}>Total Salary{onlyChanged ? ' (whole department)' : ''}</td>
                <td className="px-3 py-2 text-right text-blue-800"><Money value={d.totals.current} /></td>
                <td className="px-3 py-2 text-right"><Money value={d.totals.previous} /></td>
                <td className="px-3 py-2 text-right"><ChangeCell v={d.totals.change} base={d.totals.previous} /></td>
              </tr></tfoot>
            </table>
          </div>
        </>)}
      </DialogContent>
    </Dialog>
  );
};
